import {
  collection,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore'
import {
  getGlobalCollection,
  getInstituteCollection,
  resolveScopedCollection,
  resolveScopedDocRef,
} from '@/services/firestorePaths'
import { db } from '@/services/firebase'
import type { Module, ModuleCreatePayload, ModuleUpdatePayload } from '@/types'

/**
 * @auto-generated — DESCRIBE: What values this type allows and in which contexts it is used.
 */
export type ModuleWithScope = Module & { isGlobal: boolean }

interface ServiceError {
  code: string
  message: string
  originalError: unknown
}

function createServiceError(originalError: unknown): ServiceError {
  const err = originalError as { code?: string; message?: string }
  return {
    code: err.code ?? 'unknown',
    message: err.message ?? 'An unexpected error occurred',
    originalError,
  }
}

/**
 * Fetch a single Module document by ID and scope.
 */
export async function getModule(id: string, scope?: string | 'global'): Promise<ModuleWithScope | null> {
  try {
    if (scope) {
      const ref = resolveScopedDocRef('modules', id, scope)
      const snap = await getDoc(ref)
      if (!snap.exists()) return null
      return {
        id: snap.id,
        ...snap.data(),
        isGlobal: scope === 'global'
      } as ModuleWithScope
    }
    
    // Try global first
    const globalRef = resolveScopedDocRef('modules', id, 'global')
    const globalSnap = await getDoc(globalRef)
    if (globalSnap.exists()) {
      return {
        id: globalSnap.id,
        ...globalSnap.data(),
        isGlobal: true
      } as ModuleWithScope
    }
    
    // Fallback: search all institutes
    const institutesSnap = await getDocs(collection(db, 'institutes'))
    for (const instDoc of institutesSnap.docs) {
      const instRef = resolveScopedDocRef('modules', id, instDoc.id)
      const instSnap = await getDoc(instRef)
      if (instSnap.exists()) {
        return {
          id: instSnap.id,
          ...instSnap.data(),
          isGlobal: false
        } as ModuleWithScope
      }
    }
    
    return null
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * List all modules across all institutes.
 */
export async function listModules(): Promise<ModuleWithScope[]> {
  try {
    const institutesSnap = await getDocs(collection(db, 'institutes'))
    const [globalSnap, ...instituteSnaps] = await Promise.all([
      getDocs(getGlobalCollection('modules')),
      ...institutesSnap.docs.map((instDoc) =>
        getDocs(getInstituteCollection('modules', instDoc.id))
      )
    ])
    
    const globalModules = globalSnap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      isGlobal: true
    } as ModuleWithScope))
    
    const instituteModules = instituteSnaps.flatMap((snap) =>
      snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        isGlobal: false
      } as ModuleWithScope))
    )
    
    return [...globalModules, ...instituteModules]
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * List modules by owner institutes (includes global).
 */
export async function listModulesByOwner(institutes: string[]): Promise<ModuleWithScope[]> {
  if (institutes.length === 0) return []
  try {
    const [globalSnap, ...instituteSnaps] = await Promise.all([
      getDocs(getGlobalCollection('modules')),
      ...institutes.map((instituteId) => getDocs(getInstituteCollection('modules', instituteId)))
    ])
    
    const globalModules = globalSnap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      isGlobal: true
    } as ModuleWithScope))
    
    const instituteModules = instituteSnaps.flatMap((snap) =>
      snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        isGlobal: false
      } as ModuleWithScope))
    )
    
    return [...globalModules, ...instituteModules]
  } catch (error) {
    throw createServiceError(error)
  }
}


/**
 * Lists unit-level modules for multiple institute owners and multiple unit IDs (with client-side filtering).
 */
export async function listModulesByOwnerAndUnitIds(institutes: string[], unitIds: string[]): Promise<ModuleWithScope[]> {
  try {
    if (unitIds.length === 0 || institutes.length === 0) return []
    const modules = await listModulesByOwner(institutes)
    return modules.filter((m) => unitIds.includes(m.unitId) && m.classId === null)
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Lists unit-level modules for one unit ID scoped to owner institute IDs.
 */
export async function listModulesByOwnerAndUnitId(institutes: string[], unitId: string): Promise<ModuleWithScope[]> {
  try {
    const modules = await listModulesByOwner(institutes)
    return modules.filter((m) => m.unitId === unitId && m.classId === null)
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Lists class modules for one class ID scoped to owner institute IDs.
 */
export async function listModulesByOwnerAndClassId(institutes: string[], classId: string): Promise<ModuleWithScope[]> {
  try {
    const modules = await listModulesByOwner(institutes)
    return modules.filter((m) => m.classId === classId)
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Create a new module in the specified scope.
 */
export async function createModule(
  payload: Omit<ModuleCreatePayload, 'ownedBy'>,
  scope: string | 'global'
): Promise<string> {
  if (!scope) {
    throw new Error('[module.service] scope is required')
  }
  try {
    const { ownedBy: _, ...cleanPayload } = payload as any
    
    const ref = await addDoc(resolveScopedCollection('modules', scope), {
      ...cleanPayload,
      ...(scope !== 'global' ? { instituteId: scope } : {}),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })
    return ref.id
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Update a module in the specified scope.
 */
export async function updateModule(
  id: string,
  scope: string | 'global',
  payload: Omit<ModuleUpdatePayload, 'ownedBy'>
): Promise<void> {
  try {
    const { ownedBy: _, ...cleanPayload } = payload as any
    
    await updateDoc(resolveScopedDocRef('modules', id, scope), {
      ...cleanPayload,
      updatedAt: serverTimestamp(),
    })
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Delete a module from the specified scope.
 */
export async function deleteModule(id: string, scope: string | 'global'): Promise<void> {
  try {
    await deleteDoc(resolveScopedDocRef('modules', id, scope))
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Duplicate a module to a specific class and scope.
 */
export async function duplicateModuleToClass(
  id: string,
  sourceScope: string | 'global',
  targetScope: string | 'global',
  classId: string
): Promise<string> {
  try {
    const sourceRef = resolveScopedDocRef('modules', id, sourceScope)
    const snap = await getDoc(sourceRef)
    if (!snap.exists()) throw { code: 'not-found', message: 'Module not found' }
    const data = snap.data() as Omit<Module, 'id'>
    const ref = await addDoc(resolveScopedCollection('modules', targetScope), {
      ...data,
      classId,
      ...(targetScope !== 'global' ? { instituteId: targetScope } : { instituteId: undefined }),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })
    return ref.id
  } catch (error) {
    throw createServiceError(error)
  }
}
